<template>
  <apex-chart-card
    chart-title="Histórico de faturamento"
    loader="revenue-history-metrics-chart"
    use-export-file
    is-stacked
    :filter-by-date-period.sync="filterByDatePeriod"
    :config-update-options="configUpdateOptions"
    :chart-loading="loadingGeneralMetrics || loadingMetricsKpi"
    :chart-data="metricsKpiRevenueHistory"
    :series-data="seriesData"
    :data-for-export-csv="dataForExportCsv"
    :current-chart-data.sync="currentChartData"
    @change-char-type="chartType = $event"
  />
</template>

<script>
import { mapState } from 'vuex'
import { assertArray, localeString } from '@/config/helpers.js'
import ApexChartCard from '@/components/ApexChartCard.vue'
import { QUERY_METRICS_KPI_REVENUE_HISTORY } from '@/modules/campanhas/resultados-gerais/graphql'
import { DateTime } from 'luxon'

export default {
  name: 'RevenueHistory',
  components: {
    ApexChartCard,
  },
  props: {
    campaignIds: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    filterByDatePeriod: 'diario',
    locale: localeString,
    metricsKpiRevenueHistory: [],
    generalMetricsKpiRevenueHistory: [],
    currentChartData: [],
    chartType: 'bar',
    loadingGeneralMetrics: true,
    loadingMetricsKpi: true,
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    skipRevenueHistory() {
      return !this.campaignIds.length || this.loadingGeneralMetrics || !this.loadingMetricsKpi
    },
    defaultQueryVariables() {
      const { data } = this.stateFilters
      const [initialDate = '', finalDate = ''] = assertArray(data)
      const variables = {
        initialDate,
        finalDate,
        groupBy: this.queryGroupFilterByDatePeriod,
      }

      return variables
    },
    queryGroupFilterByDatePeriod() {
      switch (this.filterByDatePeriod) {
        case 'semanal':
          return 'WEEK'
        case 'diario':
          return 'DAY'
        default:
          return 'MONTH'
      }
    },

    configUpdateOptions() {
      return {
        tooltip: {
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const { newChartLabels, newChartData } = this.currentChartData
            let periodTitle = newChartLabels[dataPointIndex]
            const periodTotalSent = newChartData[dataPointIndex].totalSent
            const periodTotalLabel = periodTotalSent === 1 ? 'campanha estava ativa' : 'campanhas estavam ativas'

            const generalRevenues = series[0]
            const campaignRevenues = series[1]

            const revenueByPeriod = generalRevenues[dataPointIndex]
            const campaignRevenueByPeriod = campaignRevenues[dataPointIndex]
            const totalRevenueByPeriod = this.chartType === 'bar' ? campaignRevenueByPeriod + revenueByPeriod : revenueByPeriod
            const revenuePercentage = (campaignRevenueByPeriod * 100) / totalRevenueByPeriod || 0

            if (this.queryGroupFilterByDatePeriod === 'WEEK') periodTitle = `${periodTitle[0]} até ${periodTitle[1]}`
            return `
                  <v-card class="py-2">
                    <v-card-text>
                      <div class="px-4 tooltip-container">
                        <h2 class="ma-0">${periodTitle}</h2>
                        <h3 class="pa-0 ma-0">R$${this.locale(totalRevenueByPeriod, 2)}</h3>
                        <h3 class="mt-3 mb-0" style="font-weight: 700">R$${this.locale(
                          campaignRevenueByPeriod,
                          2
                        )} <span style="font-weight: 400">faturamento das campanhas</span></h3>
                        <p class="ma-0 cinza-0-text">${this.locale(revenuePercentage, 2)}% do faturamento total</p>
                      </div>
                      <hr class="cinza-l50-border" />
                      <p class="px-4 pt-4" class="cinza-d10-text"><span class="marca-1-text">${periodTotalSent} </span>${periodTotalLabel} ${
              this.getCountLabel
            }</p>
                    </v-card-text>
                  </v-card>
                `
          },
        },
      }
    },

    seriesData() {
      return [
        {
          name: 'Faturamento',
          chartDataPropertyName: this.chartType === 'bar' ? 'generalRevenues' : 'totalCampaignRevenues',
        },
        {
          name: 'Faturamento das campanhas',
          chartDataPropertyName: 'campaignRevenues',
        },
      ]
    },

    getCountLabel() {
      if (this.queryGroupFilterByDatePeriod === 'WEEK') return 'nessa semana'
      if (this.queryGroupFilterByDatePeriod === 'MONTH') return 'nesse mês'
      return 'nesse dia'
    },

    dataForExportCsv() {
      let headers = []
      let data = []
      let filename = 'historico_faturamento'
      if (this.queryGroupFilterByDatePeriod === 'WEEK') {
        headers = ['Faturamento total', 'Faturamento das campanhas', 'Campanhas disparadas', 'Data início', 'Data final']
        data = assertArray(this.metricsKpiRevenueHistory).map((revenue) => {
          return [revenue.totalCampaignRevenues, revenue.campaignRevenues, revenue.totalSent, revenue.label[0], revenue.label[1]]
        })
      } else {
        headers = ['Faturamento total', 'Faturamento das campanhas', 'Campanhas disparadas', 'Data']
        data = assertArray(this.metricsKpiRevenueHistory).map((revenue) => {
          return [revenue.totalCampaignRevenues, revenue.campaignRevenues, revenue.totalSent, revenue.label[0]]
        })
      }

      return {
        headers,
        data,
        filename,
      }
    },
  },

  watch: {
    campaignIds: {
      deep: true,
      handler: function () {
        this.metricsKpiRevenueHistory = []
        this.generalMetricsKpiRevenueHistory = []
        this.currentChartData = []
        if (this.campaignIds.length) {
          this.refetchQueries()
        }
      },
    },
    filterByDatePeriod() {
      this.metricsKpiRevenueHistory = []
      this.generalMetricsKpiRevenueHistory = []
      this.currentChartData = []
      if (this.campaignIds.length) {
        this.refetchQueries()
      }
    },
  },
  methods: {
    async refetchQueries() {
      await this.getGeneralMetricsKpiRevenueHistory()
      await this.getMetricsKpiRevenueHistory()
    },
    async getGeneralMetricsKpiRevenueHistory() {
      const variables = { ...this.defaultQueryVariables, campaignIds: [] }

      try {
        this.loadingGeneralMetrics = true
        const { data } = await this.$apollo.query({
          query: QUERY_METRICS_KPI_REVENUE_HISTORY,
          fetchPolicy: 'no-cache',
          variables,
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })

        this.generalMetricsKpiRevenueHistory = data?.metricsKpi?.revenue?.map((revenue) => {
          return {
            value: revenue?.value,
            label: [DateTime.fromFormat(revenue.date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy')],
          }
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingGeneralMetrics = false
      }
    },
    async getMetricsKpiRevenueHistory() {
      const variables = { ...this.defaultQueryVariables, campaignIds: this.campaignIds }

      try {
        this.loadingMetricsKpi = true
        const { data } = await this.$apollo.query({
          query: QUERY_METRICS_KPI_REVENUE_HISTORY,
          fetchPolicy: 'no-cache',
          variables,
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })

        const { total_active_campaigns, revenue } = data?.metricsKpi

        this.metricsKpiRevenueHistory = revenue?.map((revenue, index) => {
          return {
            totalCampaignRevenues: this.generalMetricsKpiRevenueHistory[index]?.value,
            campaignRevenues: revenue?.value,
            generalRevenues: this.generalMetricsKpiRevenueHistory[index]?.value - revenue?.value,
            totalSent: total_active_campaigns?.[index]?.value,
            label:
              revenue?.date.length === 1
                ? [DateTime.fromFormat(revenue?.date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy')]
                : [
                    DateTime.fromFormat(revenue?.date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
                    DateTime.fromFormat(revenue?.date[1], 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
                  ],
          }
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingMetricsKpi = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep .tooltip-container {
  & h3 {
    font-family: 'Lato', sans-serif;
    color: $marca-1;
    & span {
      font-weight: 400;
      color: $cinza-d10;
    }
  }
}
::v-deep .cinza-d10-text {
  color: $cinza-d10;
  font-size: 16px;
}
::v-deep .marca-1-text {
  color: $marca-1;
}
::v-deep .cinza-0-text {
  color: $cinza-0;
}
::v-deep .cinza-l50-border {
  border-color: $cinza-l50;
}
</style>
